import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { AUTHORITY_LINE } from '../data/carriage';   // ruling 307.1: the organization interprets; the GC enforces
import { collection, onSnapshot, orderBy, query, addDoc, serverTimestamp, doc, setDoc } from 'firebase/firestore';   // doc/setDoc: Record 593 (ruling 205 acknowledgment)
// ONB-6b. THE §44 ACT, ON A SCREEN. The module decides everything; this renders it and writes one
// document. Carriage, validity and what an officer is told all come from src/data/selfRepresentation.js
// so the page and the Cloud-side rules cannot come to disagree (directives section XI).
import {
  NOTICE_VERSION, SECTION_44_CITE, ACTS, MEMBER,
  noticeText, validateAct, noticeStateOf, officeMayAct,
} from '../data/selfRepresentation';
// LATE-1 (ruling 164), approved 2026-09-16 and filed 2026-09-22. One source: the module, checked against
// data\YCP_LATE-1_MEMBER_TEXT_APPROVED_2026-09-16.md by scripts\test-late1.mjs.
import {
  LATE1_TITLE, LATE1_INTRO, LATE1_CONDITIONS, LATE1_CARRIER_LINE,
  LATE1_RECOMMENDATION_TITLE, LATE1_RECOMMENDATION, LATE1_WHY_TITLE, LATE1_WHY,
} from '../data/lateOne';

const C = {
  line: '#1E3A52', panel: '#0F1E2E', text: '#F4F6F8', dim: '#8AA4BF',
  accent: '#E8A020', ok: '#27AE60', bad: '#C0392B',
};

// Record 593 (ruling 205 audit): onAckState(text) tells the page whether THIS officer must still acknowledge a served
// notice - the notice's own words while he must, '' once he has (or when nothing is asked of him).
export default function SelfRepresentationPanel({ claim, user, claimId, onAckState }) {
  const [acts, setActs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [statement, setStatement] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(false);
  // Forensic audit 2026-10-16: a failed read used to leave acts at [] with loaded true, so the panel said "No notice
  // has been served", offered the serve button and released the officer's held controls (onAckState('')). The failure
  // is now its own state, kept apart from `err` (which Cancel and a new act clear), and while it is set the panel
  // states nothing about the notice record and the page keeps the officer's controls held.
  const [readFailed, setReadFailed] = useState('');

  const id = claimId || claim?.docId || claim?.id;

  useEffect(() => {
    setReadFailed('');
    if (!id) return undefined;
    const q = query(collection(db, 'claims', id, 'selfRepresentation'), orderBy('servedAt', 'asc'));
    return onSnapshot(q,
      (snap) => { setActs(snap.docs.map((d) => ({ ...d.data(), docId: d.id }))); setReadFailed(''); setLoaded(true); },
      // A read that FAILS is not a read that found nothing. Silence here would read as "no notice
      // served", which is the one wrong answer this panel must never give.
      (e) => { setReadFailed('Could not read the §44 record: ' + (e?.message || e) + '. This is NOT "no notice served" - whether a notice is served is unknown until it can be read.'); setLoaded(true); });
  }, [id]);

  // The acts as the module wants them: servedAt is a Firestore Timestamp on the wire.
  const normalised = useMemo(() => acts.map((a) => ({
    ...a,
    servedAt: a.servedAt?.toDate ? a.servedAt.toDate().toISOString() : String(a.servedAt || ''),
  })), [acts]);

  // The §44 NOTICE state (section 363 - this was called `carriage` and is not carriage).
  const notice = useMemo(() => noticeStateOf(normalised), [normalised]);
  const office = useMemo(() => officeMayAct(normalised), [normalised]);

  // Is the signed-in account the claimant? The same two tests the security rule makes, so the screen
  // offers exactly what the rule will accept - never a button that is refused on click.
  const isClaimant = !!user && (
    (claim?.claimantUid && claim.claimantUid === user.uid)
    || (claim?.claimantEmail && user.email && claim.claimantEmail === user.email)
  );

  // Record 593 (ruling 205 audit): THE OFFICER ACKNOWLEDGES A SERVED NOTICE BEFORE HE WORKS THE CLAIM. A blocking
  // NOTICE, not a blocking rule: the acknowledgment is his own dated act (claims/{id}/selfRepresentationAcks/
  // {uid}~{noticeDocId}, firestore.rules), one per served act, and until it exists the page holds his edit, send-back
  // and advance controls. A Local or General Chairperson only - the administrator holds no representational role in
  // the claim and the rule does not take his acknowledgment.
  const isWorkingOfficer = !!user && !isClaimant && (user.role === 'local_chair' || user.role === 'general_chair');
  const servedAct = notice.notice && notice.act ? notice.act : null;
  const ackId = (isWorkingOfficer && servedAct && servedAct.docId && user.uid) ? user.uid + '~' + servedAct.docId : '';
  const [ack, setAck] = useState(null);
  const [ackLoaded, setAckLoaded] = useState(false);
  const [ackBusy, setAckBusy] = useState(false);
  useEffect(() => {
    setAck(null); setAckLoaded(false);
    if (!id || !ackId) return undefined;
    return onSnapshot(doc(db, 'claims', id, 'selfRepresentationAcks', ackId),
      (snap) => { setAck(snap.exists() ? snap.data() : null); setAckLoaded(true); },
      // A failed read is not an acknowledgment: the controls stay held, and the screen says why.
      (e) => { setErr('Could not read your acknowledgment of the §44 notice: ' + (e?.message || e) + '. It is NOT recorded as read.'); setAckLoaded(true); });
  }, [id, ackId]);
  const mustAck = !!ackId && office.mustAcknowledge && !ack;
  // Forensic audit 2026-10-16: an unreadable §44 record HOLDS a working officer's controls - a served notice he has
  // not acknowledged may be sitting in the record he cannot read. Never '' (released) on a failed read.
  const readHold = (readFailed && isWorkingOfficer)
    ? 'The §44 record for this claim could not be read, so whether the member has served notice is unknown. Your controls stay held until it can be read (reload the page).'
    : '';
  useEffect(() => {
    if (!onAckState) return;
    if (readHold) { onAckState(readHold); return; }   // forensic audit 2026-10-16: held, never released, on a failed read
    onAckState(mustAck ? office.because : '');
  }, [readHold, mustAck, office.because, onAckState]);
  const acknowledge = async () => {
    if (!ackId || !servedAct) return;
    setErr(''); setAckBusy(true);
    try {
      // THE DATE IS THE SERVER'S: the rule pins `at` to request.time.
      await setDoc(doc(db, 'claims', id, 'selfRepresentationAcks', ackId),
        { uid: user.uid, noticeDocId: servedAct.docId, at: serverTimestamp() });
    } catch (e) {
      setErr('Your acknowledgment was NOT recorded: ' + (e?.message || e) + '. Your controls on this claim stay held.');
    } finally { setAckBusy(false); }
  };

  const serve = async (act) => {
    setErr('');
    const payload = {
      claimId: id, act,
      actorUid: user?.uid || '', actorEmail: user?.email || '', actorName: user?.name || user?.email || '',
      noticeVersion: act === 'served' ? NOTICE_VERSION : '',
      statement: act === 'served' ? statement.trim() : '',
    };
    // The module's own gate, run here first so he is told at the field rather than by a refusal.
    const v = validateAct({ ...payload, now: 'pending' });
    if (!v.ok) { setErr(v.problems.join('  |  ')); return; }
    setBusy(true);
    try {
      await addDoc(collection(db, 'claims', id, 'selfRepresentation'), {
        ...payload,
        cite: SECTION_44_CITE,
        // THE DATE IS THE SERVER'S. The rule pins servedAt to request.time; a browser clock would be
        // a date a browser could backdate, and "serves reasonable written notice" turns on when.
        servedAt: serverTimestamp(),
      });
      setStatement('');
      setOpen(false);
    } catch (e) {
      setErr('Refused: ' + (e?.message || e));
    } finally { setBusy(false); }
  };

  if (!id || !loaded) return null;

  const served = notice.authority === MEMBER;

  return (
    <div style={{ border: '1px solid ' + (served ? C.accent : C.line), background: C.panel, marginBottom: 14 }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid ' + C.line, display: 'flex',
                    justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Representation — Constitution §44</div>
          <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>{SECTION_44_CITE}</div>
        </div>
        {/* Forensic audit 2026-10-16 (narration review): on a failed read the header said THE ORGANIZATION - a known state
            the platform did not know. It says NOT KNOWN until the record is read. */}
        <div style={{ fontSize: 12, fontWeight: 700, color: readFailed ? C.bad : served ? C.accent : C.dim, whiteSpace: 'nowrap' }}>
          {readFailed ? 'NOT KNOWN' : served ? 'THE MEMBER' : 'THE ORGANIZATION'}
        </div>
      </div>

      <div style={{ padding: '10px 14px', fontSize: 12, color: C.dim, lineHeight: 1.55 }}>
        {readFailed && <div style={{ color: C.bad, marginBottom: 8 }}>⚠ {readFailed}</div>}
        {err && <div style={{ color: C.bad, marginBottom: 8 }}>⚠ {err}</div>}

        {/* RULING 205: the office is TOLD, loudly, and is not locked out. */}
        {office.mustAcknowledge && !isClaimant && (
          <div style={{ background: '#2D1A00', border: '1px solid ' + C.accent, borderRadius: 6,
                        padding: '10px 12px', marginBottom: 10, color: C.accent }}>
            ⚠ {office.because}
            {/* Record 593 (ruling 205 audit): his acknowledgment, as a dated act of his own. */}
            {ackId && !ack && (
              <div style={{ marginTop: 8 }}>
                <button onClick={acknowledge} disabled={ackBusy || !ackLoaded}
                        style={{ background: (ackBusy || !ackLoaded) ? '#2A3646' : C.accent, color: '#10202F', border: 'none',
                                 padding: '6px 12px', fontSize: 12, fontWeight: 700,
                                 cursor: (ackBusy || !ackLoaded) ? 'default' : 'pointer' }}>
                  {ackBusy ? 'Recording...' : 'I have read this notice'}
                </button>
                <div style={{ fontSize: 11, marginTop: 4, color: C.dim }}>
                  Until you record that you have read it, your controls to edit, send back and advance this claim are
                  held. Recording it locks nobody out and stops no clock; it is dated and kept with the claim.
                </div>
              </div>
            )}
            {ackId && ack && (
              <div style={{ marginTop: 8, fontSize: 11, color: C.ok }}>
                You recorded that you read this notice{ack.at && ack.at.toDate ? ' on ' + ack.at.toDate().toISOString() : ''}.
              </div>
            )}
          </div>
        )}

        {/* Forensic audit 2026-10-16: never "No notice has been served" on a read that failed. */}
        {!served && !readFailed && (
          <div style={{ marginBottom: 8 }}>
            {/* Record 603: this line used to name the Organization as the carrier, which the carriage panel (Record
                594, ruling 144: the claimant carries the claim until she submits it) contradicted. The §44 notice and
                carriage are separate records; this states only what the notice record shows. */}
            No notice has been served under §44. That notice is a separate record from who carries this claim,
            which the carriage panel shows once the claim is filed.
          </div>
        )}

        {/* The acts themselves - append-only, so the list only ever grows. */}
        {normalised.length > 0 && !readFailed && (
          <div style={{ marginBottom: 10 }}>
            {normalised.map((a) => (
              <div key={a.docId} style={{ padding: '6px 0', borderBottom: '1px dotted ' + C.line }}>
                <span style={{ color: a.act === 'served' ? C.accent : C.dim, fontWeight: 700 }}>
                  {a.act === 'served' ? 'NOTICE SERVED' : 'NOTICE WITHDRAWN'}
                </span>
                <span style={{ color: C.dim }}>{'  —  ' + (a.servedAt || 'date pending') + '  —  ' + (a.actorName || a.actorEmail)}</span>
                {a.statement ? <div style={{ color: C.text, marginTop: 3, fontStyle: 'italic' }}>“{a.statement}”</div> : null}
              </div>
            ))}
          </div>
        )}

        {/* LATE-1, ruling 164: HIS right to ask for an extension himself, shown to him whether or not he
            has served notice (ruling 144 - he self-represents until carriage moves). It states what makes a
            request timely and recommends representation; it says nothing about THIS claim, which ruling 204
            forbids. */}
        {isClaimant && (
          <div style={{ border: '1px solid ' + C.line, borderRadius: 6, padding: '10px 12px',
                        marginBottom: 10, color: C.text, lineHeight: 1.55 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>{LATE1_TITLE}</div>
            <div style={{ fontSize: 12, color: C.dim }}>{LATE1_INTRO}</div>
            <ul style={{ margin: '6px 0 8px', paddingLeft: 18, fontSize: 12, color: C.dim }}>
              {LATE1_CONDITIONS.map((c) => <li key={c} style={{ marginBottom: 2 }}>{c}</li>)}
            </ul>
            <div style={{ fontSize: 12, color: C.dim, marginBottom: 8 }}>{LATE1_CARRIER_LINE}</div>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>{LATE1_RECOMMENDATION_TITLE}</div>
            {LATE1_RECOMMENDATION.map((t) => (
              <div key={t} style={{ fontSize: 12, color: C.dim, marginBottom: 6 }}>{t}</div>
            ))}
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>{LATE1_WHY_TITLE}</div>
            <div style={{ fontSize: 12, color: C.dim }}>{LATE1_WHY}</div>
          </div>
        )}

        {/* Only the claimant is offered anything. §44 is his right and it is his alone - an officer
            serving it "for" him would be the Organization deciding the member has taken the claim
            back FROM the Organization. The rule refuses it too; this simply never offers it. */}
        {/* Forensic audit 2026-10-16: no serve (or withdraw) offer while the record is unreadable - the act would be
            chosen against a state nobody has seen. */}
        {isClaimant && !served && (
          readFailed ? null : open ? (
            <div>
              <div style={{ background: '#0A1A2A', border: '1px solid #2B5C8A', borderRadius: 6,
                            padding: '12px 14px', marginBottom: 10, color: C.text, whiteSpace: 'pre-wrap' }}>
                {noticeText(NOTICE_VERSION)}
              </div>
              <div style={{ marginBottom: 8, color: C.text }}>{AUTHORITY_LINE}</div>   {/* ruling 307.1 */}
              <label className="form-label" htmlFor="s44-statement" style={{ color: C.dim }}>
                Your notice, in your own words. §44 asks for written notice — a button is not written notice.
              </label>
              <textarea aria-label="I serve notice that I represent myself in this claim."
                id="s44-statement"
                className="form-input"
                rows={4}
                value={statement}
                onChange={(e) => setStatement(e.target.value)}
                placeholder="I serve notice that I represent myself in this claim."
                style={{ width: '100%', marginBottom: 8 }}
              />
              <button onClick={() => serve(ACTS[0])} disabled={busy || !statement.trim()}
                      style={{ background: statement.trim() && !busy ? C.accent : '#2A3646', color: '#10202F',
                               border: 'none', padding: '6px 12px', fontSize: 12, fontWeight: 700,
                               cursor: statement.trim() && !busy ? 'pointer' : 'default', marginRight: 8 }}>
                {busy ? 'Serving...' : 'Serve this notice'}
              </button>
              <button onClick={() => { setOpen(false); setErr(''); }}
                      style={{ background: 'none', border: '1px solid ' + C.line, color: C.dim,
                               padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          ) : (
            <button onClick={() => setOpen(true)}
                    style={{ background: 'none', border: '1px solid ' + C.line, color: C.dim,
                             padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}>
              Represent myself in this claim (§44)
            </button>
          )
        )}

        {isClaimant && served && !readFailed && (
          <div>
            <div style={{ marginBottom: 8 }}>
              You carry this claim. You meet every time limit and you answer the carrier. Your officers
              are told, and they have not disappeared — the notice is served <i>on</i> the Organization,
              which still holds the agreement.
            </div>
            <div style={{ marginBottom: 8, color: C.text }}>{AUTHORITY_LINE}</div>   {/* ruling 307.1 */}
            <button onClick={() => serve(ACTS[1])} disabled={busy}
                    style={{ background: 'none', border: '1px solid ' + C.line, color: C.dim,
                             padding: '6px 12px', fontSize: 12, cursor: busy ? 'default' : 'pointer' }}>
              {busy ? 'Withdrawing...' : 'Withdraw my notice'}
            </button>
            <div style={{ fontSize: 11, marginTop: 6 }}>
              Withdrawing is a second act recorded beside the first. Neither erases the other.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
